import { FC, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, StackProps } from '@mui/material';
import { kebabCase } from 'lodash';

import { TextFieldProps, TextFieldWithDebounceProps } from '@components/base';
import { EnhancedTextField } from '@components/base';
import InputGrid from '@components/base/layout/InputGrid';
import { CasingInputHint } from '@components/forms';
import { kebabCasePattern } from '@utils/stringUtils';

export type EnhancedTextFieldWithSlugProps = Pick<
  TextFieldProps,
  'disabled' | 'maxLength' | 'multiline' | 'required'
> &
  Pick<TextFieldWithDebounceProps, 'delay'> & {
    error?: string;
    /**
     * Used to generate `name` and `id` for the input fields.
     */
    fieldName?: string;
    /**
     * Used to generate `id` for the input fields.
     */
    fieldsetId?: string;
    helperText?: string;
    /**
     * Common label for the fieldset
     */
    label?: string;
    onChangeValue: (value: string, slug?: string) => void;
    /**
     * If `undefined`, the slug field will not be shown.
     */
    slug?: string;
    slugError?: string;
    slugHelperText?: string;
    value: string;
    wrapperProps?: StackProps;
  };

export const EnhancedTextFieldWithSlug: FC<EnhancedTextFieldWithSlugProps> = ({
  delay,
  disabled,
  error,
  fieldName = 'field',
  fieldsetId,
  helperText,
  label,
  maxLength,
  multiline,
  onChangeValue,
  required,
  slug,
  slugError,
  slugHelperText,
  value,
  wrapperProps: { spacing = 1, ...wrapperProps } = {},
}) => {
  const { t } = useTranslation();

  const [generateSlugFromValue, setGenerateSlugFromValue] = useState(
    () => slug === kebabCase(value)
  );

  const id = useId();

  return (
    <Stack spacing={spacing} {...wrapperProps}>
      <InputGrid>
        <EnhancedTextField
          aria-label={label}
          delay={delay}
          disabled={disabled}
          error={!!error}
          helperText={error ?? helperText}
          id={[...(fieldsetId ? [fieldsetId] : []), fieldName, id].join('__')}
          label={label}
          maxLength={maxLength}
          mode='manual'
          multiline={multiline}
          name={fieldName}
          onChange={({ currentTarget: { value: newValue } }) => {
            if (generateSlugFromValue) {
              const newSlug = kebabCase(newValue);
              onChangeValue(newValue, newSlug);
            } else {
              onChangeValue(newValue);
            }
          }}
          required={required}
          value={value}
        />

        {slug != null && (
          <EnhancedTextField
            aria-label={t('slug')}
            CheckboxProps={{
              onChange: (_, checked) => {
                setGenerateSlugFromValue(checked);
                if (checked) {
                  const newSlug = kebabCase(value);
                  onChangeValue(value, newSlug);
                }
              },
              checked: generateSlugFromValue,
              label: t('autoGenerateSlug'),
            }}
            delay={delay}
            disabled={disabled}
            error={!!slugError}
            helperText={slugError ?? slugHelperText}
            id={[...(fieldsetId ? [fieldsetId] : []), fieldName, 'slug', id].join('__')}
            label={t('slug')}
            mode={generateSlugFromValue ? 'auto' : 'manual'}
            name={`${fieldName}_slug`}
            onChange={({ currentTarget: { value: slugValue } }) => onChangeValue(value, slugValue)}
            required={required}
            slotProps={{
              htmlInput: { pattern: kebabCasePattern },
              input: { endAdornment: <CasingInputHint casing='kebab' position='end' /> },
            }}
            value={slug}
          />
        )}
      </InputGrid>
    </Stack>
  );
};
